import { useState } from "react";
import { Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "react-i18next";
import { authService } from "../../services/auth.service";
import { tagOf } from "../../utils/api-error";
import { resetSchema } from "./validations/auth.schema";

// docs/web-api-spec.md §POST /auth/reset-password. An unknown email
// deliberately comes back as `invalid_otp` — do not translate it into
// anything that confirms whether the address exists.
const KNOWN_TAGS = ["invalid_otp", "expired_otp", "weak_password"];

const ResetPassword = () => {
  const { t } = useTranslation(["auth", "common"]);
  const [done, setDone] = useState(false);
  const [failureTag, setFailureTag] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({ resolver: yupResolver(resetSchema) });

  const fieldError = (message?: string) =>
    message ? (
      <p className="mt-1 text-sm text-red-600">{t(message, { min: 8 })}</p>
    ) : null;

  const onSubmit = handleSubmit(async ({ email, otp, newPassword }) => {
    setFailureTag(null);
    try {
      await authService.resetPassword(email, otp, newPassword);
      setDone(true);
    } catch (err) {
      setFailureTag(tagOf(err) ?? "generic");
    }
  });

  if (done) {
    return (
      <div className="card p-8 max-w-md mx-auto mt-10">
        <h1 className="mb-4 font-display text-2xl font-extrabold">
          {t("reset.title")}
        </h1>
        <p className="mb-6 text-sm text-ink-soft">{t("reset.done")}</p>
        <Link to="/login" className="font-bold text-teal-dark underline">
          {t("login.submit")}
        </Link>
      </div>
    );
  }

  return (
    <div className="card p-8 max-w-md mx-auto mt-10">
      <h1 className="mb-6 font-display text-2xl font-extrabold">
        {t("reset.title")}
      </h1>

      {failureTag && (
        <p
          role="alert"
          className="mb-4 rounded-2xl border border-red-200 bg-red-50 p-3 text-sm text-red-700"
        >
          {KNOWN_TAGS.includes(failureTag)
            ? t(`errors.${failureTag}`)
            : t("common:errors.generic")}
        </p>
      )}

      <form onSubmit={onSubmit} noValidate className="space-y-4">
        <div>
          <label htmlFor="email" className="mb-1 block text-sm font-bold">
            {t("fields.email")}
          </label>
          <input
            id="email"
            type="email"
            autoComplete="email"
            className="field-input"
            {...register("email")}
          />
          {fieldError(errors.email?.message)}
        </div>

        <div>
          <label htmlFor="otp" className="mb-1 block text-sm font-bold">
            {t("fields.otp")}
          </label>
          <input
            id="otp"
            inputMode="numeric"
            autoComplete="one-time-code"
            className="field-input"
            {...register("otp")}
          />
          {fieldError(errors.otp?.message)}
        </div>

        <div>
          <label
            htmlFor="newPassword"
            className="mb-1 block text-sm font-bold"
          >
            {t("fields.newPassword")}
          </label>
          <input
            id="newPassword"
            type="password"
            autoComplete="new-password"
            className="field-input"
            {...register("newPassword")}
          />
          {fieldError(errors.newPassword?.message)}
        </div>

        <button type="submit" disabled={isSubmitting} className="btn w-full">
          {t("reset.submit")}
        </button>
      </form>
    </div>
  );
};

export default ResetPassword;
